function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React, { forwardRef, useContext, useMemo, useRef } from "react";
import { injectTestId } from "@cvent/nucleus-test-automation";
import { isCarinaGlyph, remapIconSize } from "../../Icon/types";
import { ButtonGroupItemsContext, ButtonGroupContext } from "../ButtonGroup/ButtonGroup";
import { useButtonGroupItemContext } from "./index";
import { useDescendant } from "../../Descendants";
import useComposedRef from "../../../hooks/useComposedRef";
import { isFunction } from "../../../utils/is";
var useButtonGroupItemStyles = function(param) {
    var autoWidth = param.autoWidth, disabled = param.disabled, isFirst = param.isFirst, isLast = param.isLast, iconAlign = param.iconAlign, iconOnly = param.iconOnly, selected = param.selected, size = param.size, styles = param.styles;
    return useMemo(function() {
        if (!styles) {
            return {
                item: {},
                icon: {}
            };
        }
        return {
            item: _objectSpread({}, styles.base, isFirst && styles.firstButton, isLast && styles.lastButton, styles.size[size !== null && size !== void 0 ? size : 'm'], iconOnly && styles.iconOnly, (autoWidth || iconOnly) && styles.autoWidth, !disabled && {
                '&:hover': styles.hover
            }, !disabled && {
                '&:active': styles.active
            }, {
                '&:focus-visible': styles.focusVisible,
                '&:focus:not(.focus-visible)': styles.focus
            }, selected && _objectSpread({}, styles.active, {
                '&:hover': styles.active
            }), disabled && styles.disabled),
            icon: _objectSpread({}, styles.icon.base, iconAlign === 'start' && styles.icon.alignStart, iconAlign === 'end' && styles.icon.alignEnd, iconOnly && styles.icon.iconOnly)
        };
    }, [
        styles,
        selected,
        size,
        iconOnly,
        autoWidth,
        iconAlign,
        disabled
    ]);
};
var Icon = function(param) {
    var IconGlyph = param.icon, iconOnly = param.iconOnly, styles = param.styles;
    return _jsx("span", {
        css: styles,
        children: isCarinaGlyph(IconGlyph) ? _jsx(IconGlyph, {
            size: remapIconSize(iconOnly ? 'm' : 's')
        }) : _jsx(IconGlyph, {
            size: iconOnly ? 'm' : 's'
        })
    });
};
var ButtonGroupItem = forwardRef(function(_param, ref) {
    var accessibilityLabel = _param.accessibilityLabel, disabled = _param.disabled, icon = _param.icon, testID = _param.testID, text = _param.text, propDrop = _objectWithoutProperties(_param, [
        "accessibilityLabel",
        "disabled",
        "icon",
        "testID",
        "text"
    ]);
    var buttonGroupContext = useContext(ButtonGroupContext);
    var selectedProp = buttonGroupContext.selected, itemStyles = buttonGroupContext.itemStyles, setSelected = buttonGroupContext.setSelected, onChange = buttonGroupContext.onChange, size = buttonGroupContext.size, autoWidth = buttonGroupContext.autoWidth, iconAlign = buttonGroupContext.iconAlign, containerTestID = buttonGroupContext.containerTestID;
    var itemRef = useRef(null);
    var composedRef = useComposedRef(itemRef, ref);
    var _$ref;
    var index = useDescendant({
        element: itemRef.current,
        disabled: false,
        value: (_$ref = text !== null && text !== void 0 ? text : accessibilityLabel) !== null && _$ref !== void 0 ? _$ref : 'unknown'
    }, ButtonGroupItemsContext);
    var _$ref1 = useButtonGroupItemContext(), isFirst = _$ref1.isFirst, isLast = _$ref1.isLast;
    var isSelected = index === selectedProp;
    var iconOnly = icon && !text;
    var iconBefore = iconAlign === 'start';
    var iconAfter = iconAlign === 'end';
    var css = useButtonGroupItemStyles({
        styles: itemStyles,
        autoWidth: autoWidth,
        isFirst: isFirst,
        isLast: isLast,
        selected: isSelected,
        iconOnly: iconOnly,
        size: size,
        iconAlign: iconAlign,
        disabled: disabled
    });
    var handleOnClick = function() {
        if (isSelected) {
            return;
        }
        setSelected(index);
        if (isFunction(onChange)) {
            onChange(index);
        }
    };
    return _jsxs("button", _objectSpread({
        type: "button",
        ref: composedRef
    }, injectTestId(testID !== null && testID !== void 0 ? testID : containerTestID ? "".concat(containerTestID, "-item-").concat(index) : undefined), {
        "aria-label": accessibilityLabel,
        disabled: disabled,
        onClick: handleOnClick,
        css: css.item
    }, propDrop, {
        children: [
            icon && iconBefore && _jsx(Icon, {
                icon: icon,
                iconOnly: iconOnly,
                styles: css.icon
            }),
            text && _jsx("span", {
                children: text
            }),
            icon && iconAfter && _jsx(Icon, {
                icon: icon,
                iconOnly: iconOnly,
                styles: css.icon
            })
        ]
    }));
});
export { ButtonGroupItem };
export default ButtonGroupItem;
